/**
 * EmailSendPage — página de revisão e envio de uma campanha de e-mail.
 *
 * Estrutura espelhando o step "Revisão" do BroadcastStepper do WhatsApp:
 * 4 cards (Destinatários, Remetente, Conteúdo, Agendamento) + modal de
 * confirmação antes do envio. Após confirmar, redireciona pro relatório
 * com polling em tempo real.
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Send, FlaskConical, CalendarClock, Loader2, AlertCircle, Check,
  Mail, Users, User as UserIcon, FileText, Rocket,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { findInvalidLinks } from "@/lib/html-links";

export default function EmailSendPage() {
  const params = useParams<{ id: string }>();
  const campaignId = Number(params.id);
  const [, navigate] = useLocation();
  const utils = trpc.useUtils();

  const { data: campaign, isLoading } = trpc.emailCampaigns.get.useQuery(
    { id: campaignId },
    { enabled: Number.isFinite(campaignId) && campaignId > 0 },
  );

  // Sender de fallback do tenant (Settings > SendGrid). Backend usa essa
  // mesma resolução em runtime via getTenantSender — UI só reflete o
  // estado real pra não mostrar "remetente não configurado" em falso.
  const { data: tenantSender } = trpc.emailCampaigns.getSender.useQuery();

  const segmentIds = (campaign?.segmentIds as number[] | null) ?? [];
  const tagIds = (campaign?.tagIds as number[] | null) ?? [];
  const hasRecipients = segmentIds.length > 0 || tagIds.length > 0;

  const { data: recipientPreview, isFetching: previewFetching } = trpc.emailCampaigns.previewRecipients.useQuery(
    { segmentIds, tagIds },
    { enabled: hasRecipients },
  );
  const recipientCount = recipientPreview?.count ?? 0;

  const { data: tags = [] } = trpc.tags.list.useQuery();
  const { data: segments = [] } = trpc.segments.list.useQuery();
  const segmentNames = segments.filter((s: any) => segmentIds.includes(s.id)).map((s: any) => s.name);
  const tagNames = tags.filter((t: any) => tagIds.includes(t.id)).map((t: any) => t.name);

  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Data já gravada na campanha (agendada antes, ou sugerida por quem montou o
  // rascunho — o gerador de conteúdo manda assim) vem preenchida. Só a futura:
  // o passado não serve para agendar. Uma vez por campanha, para não desfazer
  // o que a pessoa mudar aqui quando a query recarregar.
  const [dataPreenchida, setDataPreenchida] = useState<number | null>(null);
  useEffect(() => {
    if (!campaign || dataPreenchida === campaign.id) return;
    setDataPreenchida(campaign.id);
    const quando = campaign.scheduledAt ? new Date(campaign.scheduledAt as unknown as string) : null;
    if (!quando || quando.getTime() <= Date.now()) return;
    const p = (n: number) => String(n).padStart(2, "0");
    setScheduleEnabled(true);
    setScheduleDate(`${quando.getFullYear()}-${p(quando.getMonth() + 1)}-${p(quando.getDate())}`);
    setScheduleTime(`${p(quando.getHours())}:${p(quando.getMinutes())}`);
  }, [campaign, dataPreenchida]);
  const [testEmail, setTestEmail] = useState("");
  const [testVars, setTestVars] = useState<Record<string, string>>({});

  // Variáveis únicas detectadas em subject + html — usadas só pra montar
  // os inputs do card "Enviar teste".
  const detectedVars = useMemo(() => {
    const subject = campaign?.subject ?? "";
    const html = (campaign as any)?.contentHtml ?? "";
    const text = `${subject}\n${html}`;
    const set = new Set<string>();
    text.replace(/\{\{(\w+)\}\}/g, (_m, key) => {
      set.add(String(key).toLowerCase());
      return "";
    });
    return Array.from(set);
  }, [campaign]);

  const VAR_PLACEHOLDERS: Record<string, string> = {
    nome: "Ex: Maria Silva",
    primeiro_nome: "Ex: Maria",
    email: "ex: maria@empresa.com",
    telefone: "Ex: 5511999999999",
    empresa: "Ex: Avante Capacitações",
    cargo: "Ex: Diretora",
    orgao: "Ex: Prefeitura de Cuiabá",
    cidade: "Ex: Cuiabá",
    estado: "Ex: MT",
    setor: "Ex: Administrativo",
  };

  // Mutation de envio: backend enfileira no pg-boss e retorna ~50ms.
  // EmailCampaignReport faz polling em getProgress a cada 3s.
  const sendMutation = trpc.emailCampaigns.send.useMutation({
    onSuccess: (data: any) => {
      if (data?.queued) {
        utils.emailCampaigns.list.invalidate();
        utils.emailCampaigns.listSent.invalidate();
        utils.emailCampaigns.getProgress.invalidate({ id: campaignId });
        return;
      }
      const sent = data?.sent ?? 0;
      const failed = data?.failed ?? 0;
      if (sent > 0 && failed === 0) toast.success(`E-mail enviado para ${sent} destinatário(s)!`);
      else if (sent > 0 && failed > 0) toast.warning(`Enviado para ${sent}. ${failed} falharam.`);
      else if (data?.errors?.[0]) toast.error(`Falha: ${data.errors[0].error}`);
      utils.emailCampaigns.list.invalidate();
      utils.emailCampaigns.listSent.invalidate();
      utils.emailCampaigns.getProgress.invalidate({ id: campaignId });
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao enviar")),
  });

  const sendTestMutation = trpc.emailCampaigns.sendTest.useMutation({
    onSuccess: (_, vars) => toast.success(`Teste enviado para ${vars.email}`),
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao enviar teste")),
  });

  const scheduleMutation = trpc.emailCampaigns.update.useMutation({
    onSuccess: () => {
      toast.success("E-mail agendado.");
      utils.emailCampaigns.list.invalidate();
      navigate("/emails");
    },
    onError: (err) => toast.error(toUserFriendlyError(err, "Erro ao agendar")),
  });

  // Resolução do remetente espelhando o backend: campanha tem precedência;
  // se vazio, usa sender do tenant. Antes a UI mostrava "não configurado"
  // mesmo quando o tenant tinha sender — bug.
  const senderName = ((campaign?.senderName ?? "").trim() || (tenantSender?.senderName ?? "").trim());
  const senderEmail = ((campaign?.senderEmail ?? "").trim() || (tenantSender?.senderEmail ?? "").trim());
  const usesTenantSender = !campaign?.senderEmail && !!tenantSender?.senderEmail;

  // Validação do agendamento usada tanto pelos cards quanto pelo botão
  // principal — calculada antes dos handlers pra evitar dependência circular.
  const scheduledIso = scheduleDate && scheduleTime
    ? new Date(`${scheduleDate}T${scheduleTime}`).toISOString()
    : null;
  const scheduledIsValid = scheduledIso ? new Date(scheduledIso).getTime() > Date.now() : false;

  // Links inválidos no HTML do corpo (href="", "https://", "#", protocolos
  // perigosos). Detecta o caso recorrente do editor que salva botões com
  // href="https://" como template default — campanha "teste2 Matheus" foi
  // enviada assim e nenhum click chegou ao SendGrid. useMemo pra não rodar
  // a regex em cada render.
  const invalidLinks = useMemo(
    () => findInvalidLinks((campaign as any)?.contentHtml ?? ""),
    // Dep só no contentHtml — qualquer outro campo de campaign mudar não
    // afeta a validação de links. Antes era [campaign] (objeto inteiro),
    // que disparava recomputação a cada refetch mesmo sem mudança de HTML.
    [(campaign as any)?.contentHtml],
  );

  const ready = {
    name: !!campaign?.name?.trim(),
    subject: !!campaign?.subject?.trim(),
    sender: !!senderEmail,
    recipients: hasRecipients && recipientCount > 0,
    schedule: !scheduleEnabled || scheduledIsValid,
    links: invalidLinks.length === 0,
  };
  const allOk = ready.name && ready.subject && ready.sender && ready.recipients && ready.schedule && ready.links;

  const handleSendNow = async () => {
    try {
      await sendMutation.mutateAsync({ id: campaignId });
      // Modal só fecha em sucesso — em erro fica aberta pra dar feedback
      // no contexto da ação (toast de erro já é mostrado pelo onError).
      setConfirmOpen(false);
      toast.info("Envio iniciado. Acompanhe o progresso em tempo real abaixo.");
      navigate(`/emails/campaigns/${campaignId}`);
    } catch {
      // onError já mostrou o toast — modal continua aberta.
    }
  };

  const handleSchedule = () => {
    // Defesa em profundidade: o botão do header já desabilita quando
    // !allOk, mas validamos de novo aqui caso outro caminho chame.
    if (!allOk) {
      toast.warning("Confira os itens em vermelho antes de agendar.");
      return;
    }
    if (!scheduledIsValid) {
      toast.warning("Defina data e hora futuras.");
      return;
    }
    scheduleMutation.mutate({ id: campaignId, status: "scheduled", scheduledAt: scheduledIso! });
    setConfirmOpen(false);
  };

  const handleSendTest = () => {
    if (!testEmail.trim()) {
      toast.warning("Informe um e-mail para o teste.");
      return;
    }
    sendTestMutation.mutate({
      id: campaignId,
      email: testEmail.trim(),
      vars: detectedVars.length > 0 ? testVars : undefined,
    });
  };

  if (isLoading) {
    return (
      <PermissionGuard permission="email:send_campaign">
        <DashboardLayout>
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </DashboardLayout>
      </PermissionGuard>
    );
  }

  if (!campaign) {
    return (
      <PermissionGuard permission="email:send_campaign">
        <DashboardLayout>
          <div className="text-center py-32 space-y-3">
            <p className="text-muted-foreground">Campanha não encontrada.</p>
            <Button variant="outline" onClick={() => navigate("/emails")}>Voltar</Button>
          </div>
        </DashboardLayout>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard permission="email:send_campaign">
      <DashboardLayout>
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b -mx-4 px-4 sm:-mx-6 sm:px-6 py-3 mb-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={() => navigate(`/emails/${campaignId}/editor`)}>
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar ao editor
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold truncate">Revisar e enviar</h1>
                <p className="text-xs text-muted-foreground truncate">{campaign.name}</p>
              </div>
            </div>
            <Button
              size="lg"
              className="gap-2 shrink-0"
              onClick={() => setConfirmOpen(true)}
              disabled={!allOk}
            >
              <Rocket className="h-4 w-4" />
              {scheduleEnabled ? "Agendar envio" : "Enviar agora"}
            </Button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto pb-12 space-y-6">
          {/* 4 cards de revisão — espelha o step "Revisão" do BroadcastStepper */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ReviewCard
              icon={Users}
              title="Destinatários"
              ok={ready.recipients}
              footer={
                hasRecipients
                  ? <span className="font-medium text-foreground">~{previewFetching ? "…" : recipientCount} contato{recipientCount === 1 ? "" : "s"}</span>
                  : <span className="text-amber-700">Configure segmentos ou tags no editor</span>
              }
            >
              {hasRecipients ? (
                <div className="space-y-1.5">
                  {segmentNames.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {segmentNames.map((n: string) => (
                        <Badge key={`s-${n}`} variant="secondary" className="text-xs">Segmento: {n}</Badge>
                      ))}
                    </div>
                  )}
                  {tagNames.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {tagNames.map((n: string) => (
                        <Badge key={`t-${n}`} variant="outline" className="text-xs">Tag: {n}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Nenhum segmento ou tag selecionado.</p>
              )}
            </ReviewCard>

            <ReviewCard
              icon={UserIcon}
              title="Remetente"
              ok={ready.sender}
              footer={
                senderEmail
                  ? <span className="font-medium text-foreground truncate block">{senderName ? `${senderName} <${senderEmail}>` : senderEmail}</span>
                  : <span className="text-amber-700">Configure em Configurações &gt; Integração</span>
              }
            >
              {usesTenantSender && senderEmail && (
                <Badge variant="outline" className="text-xs">Padrão do tenant</Badge>
              )}
            </ReviewCard>

            <ReviewCard
              icon={FileText}
              title="Conteúdo"
              ok={ready.name && ready.subject && ready.links}
              footer={
                <div className="space-y-0.5">
                  <div className="text-xs text-muted-foreground">Assunto</div>
                  <div className="text-sm truncate" title={campaign.subject ?? ""}>
                    {campaign.subject || <span className="text-amber-700">(faltando)</span>}
                  </div>
                </div>
              }
            >
              {invalidLinks.length > 0 && (
                <div className="mb-2 rounded-md border border-red-200 bg-red-50 p-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                    <div className="text-xs">
                      <p className="font-medium text-red-800">
                        {invalidLinks.length === 1 ? "1 link inválido" : `${invalidLinks.length} links inválidos`} no e-mail
                      </p>
                      <ul className="mt-1 text-red-700 list-disc list-inside space-y-0.5">
                        {invalidLinks.slice(0, 3).map((l, i) => (
                          <li key={i}>{l.reason}</li>
                        ))}
                        {invalidLinks.length > 3 && (
                          <li>e mais {invalidLinks.length - 3}…</li>
                        )}
                      </ul>
                      <p className="mt-1 text-red-700">
                        Volte ao editor e corrija os links antes de enviar.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {detectedVars.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">Variáveis no template:</p>
                  <div className="flex flex-wrap gap-1">
                    {detectedVars.map((v) => (
                      <Badge key={v} variant="secondary" className="text-[10px] font-mono">{`{{${v}}}`}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </ReviewCard>

            <ReviewCard
              icon={CalendarClock}
              title="Agendamento"
              ok={ready.schedule}
              footer={
                scheduleEnabled && scheduledIsValid ? (
                  <span className="font-medium text-foreground">
                    {new Date(scheduledIso!).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                ) : scheduleEnabled ? (
                  <span className="text-amber-700">Defina data e hora futuras</span>
                ) : (
                  <span className="font-medium text-foreground">Disparo imediato</span>
                )
              }
            >
              <div className="flex items-center gap-2 mb-2">
                <Switch id="schedule" checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} />
                <Label htmlFor="schedule" className="text-xs cursor-pointer">Agendar para depois</Label>
              </div>
              {scheduleEnabled && (
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            </ReviewCard>
          </div>

          {/* Card de teste — só aparece pra dar uma última conferida no template */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-muted-foreground" />
                Enviar teste
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Manda uma cópia para um e-mail específico antes do envio em massa.
                {detectedVars.length > 0 && " Preencha valores fictícios pras variáveis abaixo."}
              </p>

              {detectedVars.length > 0 && (
                <div className="rounded-lg border bg-muted/20 p-3 space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Variáveis detectadas</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {detectedVars.map((varName) => (
                      <div key={varName}>
                        <Label className="text-xs font-mono">{`{{${varName}}}`}</Label>
                        <Input
                          value={testVars[varName] ?? ""}
                          onChange={(e) => setTestVars((s) => ({ ...s, [varName]: e.target.value }))}
                          placeholder={VAR_PLACEHOLDERS[varName] ?? "(deixe vazio)"}
                          className="mt-1 h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={handleSendTest}
                  disabled={sendTestMutation.isPending || !testEmail.trim()}
                  className="gap-1.5"
                >
                  {sendTestMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />}
                  Enviar teste
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modal de confirmação — espelha BroadcastStepper */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {scheduleEnabled ? <CalendarClock className="h-5 w-5" /> : <Send className="h-5 w-5" />}
                {scheduleEnabled ? "Agendar envio" : "Enviar agora"}
              </DialogTitle>
              <DialogDescription>
                {scheduleEnabled
                  ? `Será enviado em ${scheduledIso ? new Date(scheduledIso).toLocaleString("pt-BR") : "—"} para ~${recipientCount} contato${recipientCount === 1 ? "" : "s"}.`
                  : `Vai disparar agora para ~${recipientCount} contato${recipientCount === 1 ? "" : "s"}. Você é redirecionado pra tela de progresso — não precisa ficar esperando.`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
              {scheduleEnabled ? (
                <Button onClick={handleSchedule} disabled={scheduleMutation.isPending || !scheduledIsValid}>
                  {scheduleMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarClock className="h-4 w-4 mr-1.5" />}
                  Agendar
                </Button>
              ) : (
                <Button onClick={handleSendNow} disabled={sendMutation.isPending}>
                  {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                  Confirmar envio
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </PermissionGuard>
  );
}

function ReviewCard({
  icon: Icon,
  title,
  ok,
  footer,
  children,
}: {
  icon: any;
  title: string;
  ok: boolean;
  footer?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <Card className={cn(ok ? "border-emerald-200" : "border-amber-200")}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Icon className={cn("h-4 w-4", ok ? "text-emerald-600" : "text-amber-600")} />
          {title}
          {ok
            ? <Check className="h-3.5 w-3.5 text-emerald-600" />
            : <AlertCircle className="h-3.5 w-3.5 text-amber-600" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {children}
        {footer && <div className="text-sm">{footer}</div>}
      </CardContent>
    </Card>
  );
}
